import React, { useState } from 'react';
import {
  Grid,
  Paper,
  Box,
  Typography,
  Chip,
  ButtonGroup,
  Button,
  Divider,
  LinearProgress,
} from '@material-ui/core';
import {
  Star,
  Build,
  People,
  SupervisorAccount,
  Warning,
} from '@material-ui/icons';
import { formattedCurrency } from '../../../utils/utils';

const OverviewRankingsSection = ({ rankings }) => {
  const [rightTab, setRightTab] = useState('customers');

  if (!rankings) return null;

  const {
    topProducts = [],
    topServices = [],
    topCustomers = [],
    topSellers = [],
  } = rankings;

  return (
    <Grid container spacing={3} style={{ marginBottom: 24 }}>
      {/* Coluna Esquerda: Top Produtos Vendidos */}
      <Grid item xs={12} md={6}>
        <Paper
          elevation={0}
          style={{
            padding: 20,
            borderRadius: 10,
            border: '1px solid #e2e8f0',
            height: '100%',
          }}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center">
              <Star style={{ color: '#f59e0b', marginRight: 8 }} />
              <Box>
                <Typography
                  variant="h6"
                  style={{ fontWeight: 700, fontSize: '1.05rem', color: '#0f172a' }}
                >
                  Principais Produtos Vendidos
                </Typography>
                <Typography variant="caption" style={{ color: '#64748b' }}>
                  Ranking por faturamento, margem bruta e saldo de estoque atual
                </Typography>
              </Box>
            </Box>
            <Chip
              size="small"
              label={`Top ${topProducts.length}`}
              style={{
                backgroundColor: '#fef3c7',
                color: '#b45309',
                fontWeight: 700,
              }}
            />
          </Box>

          <Divider style={{ marginBottom: 16 }} />

          {topProducts.length > 0 ? (
            <Box>
              {topProducts.map((prod, idx) => {
                const isRupture = prod.lacking || Number(prod.currentStock) <= 0;
                return (
                  <Box
                    key={prod.id || idx}
                    py={1.25}
                    style={{
                      borderBottom:
                        idx < topProducts.length - 1 ? '1px solid #f1f5f9' : 'none',
                    }}
                  >
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={0.5}
                    >
                      <Box display="flex" alignItems="center" minWidth={0} pr={1}>
                        <Box
                          display="inline-flex"
                          alignItems="center"
                          justifyContent="center"
                          style={{
                            width: 24,
                            height: 24,
                            borderRadius: 6,
                            backgroundColor: idx < 3 ? '#dbeafe' : '#f1f5f9',
                            color: idx < 3 ? '#1d4ed8' : '#64748b',
                            fontWeight: 800,
                            fontSize: '0.75rem',
                            marginRight: 10,
                            flexShrink: 0,
                          }}
                        >
                          {idx + 1}
                        </Box>
                        <Typography
                          variant="body2"
                          noWrap
                          style={{ fontWeight: 700, color: '#0f172a' }}
                        >
                          {prod.name}
                        </Typography>
                      </Box>

                      <Typography
                        variant="body2"
                        style={{
                          fontWeight: 800,
                          color: '#0f172a',
                          flexShrink: 0,
                        }}
                      >
                        {formattedCurrency(prod.revenue)}
                      </Typography>
                    </Box>

                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      flexWrap="wrap"
                      pl={4.25}
                      mb={0.75}
                    >
                      <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                        <Typography variant="caption" style={{ color: '#475569', fontWeight: 600 }}>
                          {prod.quantity} {prod.unit || 'UN'} vendidos
                        </Typography>
                        <Chip
                          size="small"
                          label={`Margem ${prod.marginPct}%`}
                          style={{
                            height: 20,
                            fontSize: '0.68rem',
                            fontWeight: 700,
                            backgroundColor:
                              prod.marginPct >= 30
                                ? '#dcfce7'
                                : prod.marginPct >= 15
                                ? '#fef9c3'
                                : '#fee2e2',
                            color:
                              prod.marginPct >= 30
                                ? '#15803d'
                                : prod.marginPct >= 15
                                ? '#854d0e'
                                : '#b91c1c',
                          }}
                        />
                      </Box>

                      <Box display="flex" alignItems="center">
                        {isRupture ? (
                          <Chip
                            size="small"
                            icon={<Warning style={{ fontSize: 12, color: '#b91c1c' }} />}
                            label={`Estoque: ${prod.currentStock}`}
                            style={{
                              height: 20,
                              fontSize: '0.68rem',
                              fontWeight: 700,
                              backgroundColor: '#fee2e2',
                              color: '#b91c1c',
                            }}
                          />
                        ) : (
                          <Typography variant="caption" style={{ color: '#64748b' }}>
                            Estoque: <strong>{prod.currentStock}</strong>
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    <Box pl={4.25} display="flex" alignItems="center">
                      <Box flex={1} mr={1}>
                        <LinearProgress
                          variant="determinate"
                          value={Math.min(100, prod.sharePct || 0)}
                          style={{
                            height: 4,
                            borderRadius: 2,
                            backgroundColor: '#f1f5f9',
                          }}
                        />
                      </Box>
                      <Typography
                        variant="caption"
                        style={{ color: '#94a3b8', fontSize: '0.7rem' }}
                      >
                        {prod.sharePct}%
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ) : (
            <Box py={6} textAlign="center" style={{ color: '#94a3b8' }}>
              <Typography variant="body2">
                Nenhum produto vendido no período selecionado.
              </Typography>
            </Box>
          )}
        </Paper>
      </Grid>

      {/* Coluna Direita: Clientes, Vendedores e Serviços */}
      <Grid item xs={12} md={6}>
        <Paper
          elevation={0}
          style={{
            padding: 20,
            borderRadius: 10,
            border: '1px solid #e2e8f0',
            height: '100%',
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            mb={2}
          >
            <Box display="flex" alignItems="center">
              {rightTab === 'customers' && (
                <People style={{ color: '#2563eb', marginRight: 8 }} />
              )}
              {rightTab === 'sellers' && (
                <SupervisorAccount style={{ color: '#7c3aed', marginRight: 8 }} />
              )}
              {rightTab === 'services' && (
                <Build style={{ color: '#059669', marginRight: 8 }} />
              )}
              <Box>
                <Typography
                  variant="h6"
                  style={{ fontWeight: 700, fontSize: '1.05rem', color: '#0f172a' }}
                >
                  {rightTab === 'customers' && 'Principais Clientes do Período'}
                  {rightTab === 'sellers' && 'Desempenho por Vendedor / Operador'}
                  {rightTab === 'services' && 'Principais Serviços Realizados'}
                </Typography>
                <Typography variant="caption" style={{ color: '#64748b' }}>
                  {rightTab === 'customers' && 'Clientes com maior volume de compras no período'}
                  {rightTab === 'sellers' && 'Volume de vendas e ticket médio por operador'}
                  {rightTab === 'services' && 'Serviços mais executados e receita gerada'}
                </Typography>
              </Box>
            </Box>

            <ButtonGroup size="small" style={{ marginTop: 4 }}>
              <Button
                variant={rightTab === 'customers' ? 'contained' : 'outlined'}
                color="primary"
                onClick={() => setRightTab('customers')}
              >
                Clientes
              </Button>
              <Button
                variant={rightTab === 'sellers' ? 'contained' : 'outlined'}
                color="primary"
                onClick={() => setRightTab('sellers')}
              >
                Equipe
              </Button>
              <Button
                variant={rightTab === 'services' ? 'contained' : 'outlined'}
                color="primary"
                onClick={() => setRightTab('services')}
              >
                Serviços
              </Button>
            </ButtonGroup>
          </Box>

          <Divider style={{ marginBottom: 16 }} />

          {rightTab === 'customers' && (
            <Box>
              {topCustomers.length > 0 ? (
                topCustomers.map((cust, idx) => (
                  <Box
                    key={cust.id || idx}
                    py={1.25}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    style={{
                      borderBottom:
                        idx < topCustomers.length - 1 ? '1px solid #f1f5f9' : 'none',
                    }}
                  >
                    <Box display="flex" alignItems="center" minWidth={0} pr={1}>
                      <Box
                        display="inline-flex"
                        alignItems="center"
                        justifyContent="center"
                        style={{
                          width: 24,
                          height: 24,
                          borderRadius: 6,
                          backgroundColor: cust.isWalkIn ? '#f1f5f9' : '#e0f2fe',
                          color: cust.isWalkIn ? '#64748b' : '#0369a1',
                          fontWeight: 800,
                          fontSize: '0.75rem',
                          marginRight: 10,
                          flexShrink: 0,
                        }}
                      >
                        {idx + 1}
                      </Box>
                      <Box minWidth={0}>
                        <Typography
                          variant="body2"
                          noWrap
                          style={{ fontWeight: 700, color: '#0f172a' }}
                        >
                          {cust.name}
                        </Typography>
                        <Typography variant="caption" style={{ color: '#64748b' }}>
                          {cust.salesCount} {cust.salesCount === 1 ? 'compra' : 'compras'} • Ticket médio: {formattedCurrency(cust.averageTicket)}
                        </Typography>
                      </Box>
                    </Box>

                    <Typography
                      variant="body2"
                      style={{ fontWeight: 800, color: '#0f172a', flexShrink: 0 }}
                    >
                      {formattedCurrency(cust.totalSpent)}
                    </Typography>
                  </Box>
                ))
              ) : (
                <Box py={6} textAlign="center" style={{ color: '#94a3b8' }}>
                  <Typography variant="body2">
                    Nenhum cliente com compras no período.
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {rightTab === 'sellers' && (
            <Box>
              {topSellers.length > 0 ? (
                topSellers.map((sel, idx) => (
                  <Box
                    key={sel.id || idx}
                    py={1.25}
                    style={{
                      borderBottom:
                        idx < topSellers.length - 1 ? '1px solid #f1f5f9' : 'none',
                    }}
                  >
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={0.5}
                    >
                      <Box display="flex" alignItems="center">
                        <Box
                          display="inline-flex"
                          alignItems="center"
                          justifyContent="center"
                          style={{
                            width: 24,
                            height: 24,
                            borderRadius: 6,
                            backgroundColor: '#ede9fe',
                            color: '#6d28d9',
                            fontWeight: 800,
                            fontSize: '0.75rem',
                            marginRight: 10,
                          }}
                        >
                          {idx + 1}
                        </Box>
                        <Box>
                          <Typography
                            variant="body2"
                            style={{ fontWeight: 700, color: '#0f172a' }}
                          >
                            {sel.name}
                          </Typography>
                          <Typography variant="caption" style={{ color: '#64748b' }}>
                            {sel.salesCount} {sel.salesCount === 1 ? 'venda' : 'vendas'} • Ticket médio: {formattedCurrency(sel.averageTicket)}
                          </Typography>
                        </Box>
                      </Box>

                      <Box textAlign="right">
                        <Typography
                          variant="body2"
                          style={{ fontWeight: 800, color: '#0f172a' }}
                        >
                          {formattedCurrency(sel.totalSold)}
                        </Typography>
                        <Typography variant="caption" style={{ color: '#64748b' }}>
                          {sel.sharePct}% do total
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                ))
              ) : (
                <Box py={6} textAlign="center" style={{ color: '#94a3b8' }}>
                  <Typography variant="body2">
                    Nenhuma venda por operador registrada no período.
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {rightTab === 'services' && (
            <Box>
              {topServices.length > 0 ? (
                topServices.map((srv, idx) => (
                  <Box
                    key={srv.id || idx}
                    py={1.25}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    style={{
                      borderBottom:
                        idx < topServices.length - 1 ? '1px solid #f1f5f9' : 'none',
                    }}
                  >
                    <Box display="flex" alignItems="center" minWidth={0} pr={1}>
                      <Box
                        display="inline-flex"
                        alignItems="center"
                        justifyContent="center"
                        style={{
                          width: 24,
                          height: 24,
                          borderRadius: 6,
                          backgroundColor: '#d1fae5',
                          color: '#047857',
                          fontWeight: 800,
                          fontSize: '0.75rem',
                          marginRight: 10,
                          flexShrink: 0,
                        }}
                      >
                        {idx + 1}
                      </Box>
                      <Box minWidth={0}>
                        <Typography
                          variant="body2"
                          noWrap
                          style={{ fontWeight: 700, color: '#0f172a' }}
                        >
                          {srv.name}
                        </Typography>
                        <Typography variant="caption" style={{ color: '#64748b' }}>
                          {srv.quantity} {srv.quantity === 1 ? 'execução' : 'execuções'} • {srv.sharePct}% das vendas
                        </Typography>
                      </Box>
                    </Box>

                    <Typography
                      variant="body2"
                      style={{ fontWeight: 800, color: '#0f172a', flexShrink: 0 }}
                    >
                      {formattedCurrency(srv.revenue)}
                    </Typography>
                  </Box>
                ))
              ) : (
                <Box py={6} textAlign="center" style={{ color: '#94a3b8' }}>
                  <Typography variant="body2">
                    Nenhum serviço realizado no período selecionado.
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
};

export default OverviewRankingsSection;
